<template>
    <div class="enquiries-widget" :class="{ 'modal-context': isModal }">
        <!-- Content Area -->
        <div class="content-area with-toggle">
            <!-- AI FAQ Mode -->
            <div v-if="mode === 'ai'" class="ai-mode">
                <!-- Customer Service Agent -->
                <customer-service-agent
                    :occasion-id="occasionId"
                    :participant-id="participantId"
                    :show-metadata="showMetadata"
                    :max-questions="maxQuestions"
                    :api-endpoint="apiEndpoint"
                    :logo-url="logoUrl"
                    :logo-alt="logoAlt"
                    :welcome-message="welcomeMessage"
                    @question-submitted="$emit('question-submitted', $event)"
                    @response-received="$emit('response-received', $event)"
                    @error-occurred="$emit('error-occurred', $event)"
                    @rate-limit-exceeded="$emit('rate-limit-exceeded', $event)"
                    class="customer-service-clean"
                />
            </div>

            <!-- FAQ List Mode -->
            <div v-if="mode === 'faq-list'" class="faq-list-mode">
                <!-- FAQ Search or Back Button -->
                <div class="faq-header-area">
                    <!-- FAQ Search (when in list view) -->
                    <div v-if="showFaqSearch && !isViewingFaqDetail" class="faq-search">
                        <div class="custom-search-bar">
                            <i class="fas fa-search search-icon"></i>
                            <input
                                v-model="faqSearchTerm"
                                type="text"
                                class="search-input"
                                placeholder="Search FAQs..."
                                @input="onFaqSearchInput"
                                @keyup.enter="searchFaqs"
                            />
                            <button
                                v-if="faqSearchTerm"
                                class="clear-btn"
                                type="button"
                                @click="clearFaqSearch"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Back Button (when viewing FAQ detail) -->
                    <div v-if="isViewingFaqDetail" class="faq-back-area">
                        <button
                            class="btn btn-link faq-back-btn"
                            @click="handleFaqBackToList"
                            type="button"
                        >
                            <i class="fas fa-arrow-left"></i> Back to FAQs
                        </button>
                    </div>
                </div>

                <!-- Loading State -->
                <div v-if="faqsLoading" class="text-center p-3">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="sr-only">Loading FAQs...</span>
                    </div>
                    <div class="mt-2 text-muted">
                        {{ faqSearchTerm ? 'Searching FAQs...' : 'Loading FAQs...' }}
                    </div>
                </div>

                <!-- No FAQs State -->
                <div v-else-if="faqsLoaded && faqs.length === 0" class="text-center p-3 text-muted">
                    <i class="fas fa-question-circle mb-2"></i>
                    <div v-if="faqSearchTerm">
                        No FAQs found for "{{ faqSearchTerm }}"
                    </div>
                    <div v-else>
                        No FAQs available at the moment.
                    </div>
                    <div class="mt-2">
                        <button
                            v-if="faqSearchTerm"
                            class="btn btn-sm btn-outline-secondary me-2"
                            @click="clearFaqSearch"
                        >
                            Clear Search
                        </button>
                        <button
                            class="btn btn-sm btn-outline-primary"
                            @click="switchToContact"
                        >
                            Contact Us Instead
                        </button>
                    </div>
                </div>

                <!-- FAQ List -->
                <faqs-list
                    v-else-if="faqs.length > 0"
                    ref="faqsList"
                    :faqs="faqs"
                    :id-extension="idExtension"
                    :show-back-button="false"
                    @faq-opened="handleFaqOpened"
                    @faq-closed="handleFaqClosed"
                />
            </div>

            <!-- Traditional Contact Mode -->
            <div v-if="mode === 'contact'" class="contact-mode">
                <enquiries-form
                    :occasion-id="occasionId"
                    @contact-submitted="$emit('contact-submitted', $event)"
                    @contact-success="$emit('contact-success', $event)"
                    @contact-error="$emit('contact-error', $event)"
                />
            </div>
        </div>

        <!-- Sticky Mode Toggle at Bottom -->
        <div class="mode-toggle-sticky">
            <div class="mode-tabs" role="group">
                <div
                    v-if="aiEnabled"
                    class="mode-tab"
                    :class="{ 'active': mode === 'ai' }"
                    @click="mode = 'ai'"
                    role="button"
                    tabindex="0"
                    @keydown.enter="mode = 'ai'"
                    @keydown.space.prevent="mode = 'ai'"
                >
<!--                    <i class="fas fa-robot"></i>-->
                    <span class="tab-text">Ask Us</span>
                </div>
                <div
                    v-if="faqListEnabled"
                    class="mode-tab"
                    :class="{ 'active': mode === 'faq-list' }"
                    @click="switchToFaqList"
                    role="button"
                    tabindex="0"
                    @keydown.enter="switchToFaqList"
                    @keydown.space.prevent="switchToFaqList"
                >
<!--                    <i class="fas fa-list"></i>-->
                    <span class="tab-text">FAQs</span>
                </div>
                <div
                    class="mode-tab"
                    :class="{ 'active': mode === 'contact' }"
                    @click="mode = 'contact'"
                    role="button"
                    tabindex="0"
                    @keydown.enter="mode = 'contact'"
                    @keydown.space.prevent="mode = 'contact'"
                >
<!--                    <i class="fas fa-envelope"></i>-->
                    <span class="tab-text">Contact</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomerServiceAgent from '../AiAgents/CustomerService/CustomerServiceAgent.vue'
import EnquiriesForm from '../Enquiries/EnquiriesForm.vue'
import FaqsList from '../Faqs/List.vue'

export default {
    name: 'Messenger',

    components: {
        CustomerServiceAgent,
        EnquiriesForm,
        FaqsList
    },

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        },
        defaultMode: {
            type: String,
            default: 'ai',
            validator: value => ['ai', 'faq-list', 'contact'].includes(value)
        },
        aiEnabled: {
            type: Boolean,
            default: true
        },
        faqListEnabled: {
            type: Boolean,
            default: true
        },
        showTitle: {
            type: Boolean,
            default: true
        },
        showFaqSearch: {
            type: Boolean,
            default: true
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 5
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },
        faqEndpoint: {
            type: String,
            default: null
        },
        idExtension: {
            type: String,
            default: 'modal'
        },
        participantId: {
            type: [Number, String],
            default: null
        },
        isModal: {
            type: Boolean,
            default: false
        },
        logoUrl: {
            type: String,
            default: null
        },
        logoAlt: {
            type: String,
            default: 'Logo'
        },
        welcomeMessage: {
            type: String,
            default: 'If you have any questions, you can ask me'
        }
    },

    data() {
        return {
            mode: this.defaultMode,
            faqs: [],
            faqsLoaded: false,
            faqsLoading: false,
            faqSearchTerm: '',
            faqSearchTimeout: null,
            showContactForm: true,
            isViewingFaqDetail: false
        }
    },

    computed: {
        computedFaqEndpoint() {
            let endpoint = this.faqEndpoint || `/api/v0/faqs/occasion/${this.occasionId}`

            // Only add search parameter if search term exists and is not empty
            if (this.faqSearchTerm && this.faqSearchTerm.trim() !== '') {
                const params = new URLSearchParams()
                params.append('question_or_answer', this.faqSearchTerm.trim())
                endpoint += `?${params.toString()}`
            }

            return endpoint
        }
    },

    methods: {
        switchToAi() {
            this.mode = 'ai'
        },

        switchToFaqList() {
            this.mode = 'faq-list'
            // Load FAQs immediately when switching to FAQ list mode
            if (!this.faqsLoaded && !this.faqsLoading) {
                this.loadAllFaqs()
            }
        },

        switchToContact() {
            this.mode = 'contact'
        },

        async loadAllFaqs() {
            if (this.faqsLoading) return

            this.faqsLoading = true
            try {
                // Load all FAQs without search parameters
                const endpoint = this.faqEndpoint || `/api/v0/faqs/occasion/${this.occasionId}`
                const response = await fetch(endpoint)

                if (response.ok) {
                    const responseData = await response.json()
                    // Handle the nested response structure: { status: "success", data: { faqs: [...] } }
                    if (responseData.status === 'success' && responseData.data && responseData.data.faqs) {
                        this.faqs = responseData.data.faqs
                    } else {
                        // Fallback for different response structures
                        this.faqs = responseData.faqs || responseData.data || responseData || []
                    }
                } else {
                    console.warn('Failed to load FAQs:', response.statusText)
                    this.faqs = []
                }
            } catch (error) {
                console.error('Error loading FAQs:', error)
                this.faqs = []
            } finally {
                this.faqsLoaded = true
                this.faqsLoading = false
            }
        },

        async loadFaqs(isSearch = false) {
            if (this.faqsLoading) return

            this.faqsLoading = true
            try {
                const response = await fetch(this.computedFaqEndpoint)
                if (response.ok) {
                    const responseData = await response.json()
                    // Handle the nested response structure: { status: "success", data: { faqs: [...] } }
                    if (responseData.status === 'success' && responseData.data && responseData.data.faqs) {
                        this.faqs = responseData.data.faqs
                    } else {
                        // Fallback for different response structures
                        this.faqs = responseData.faqs || responseData.data || responseData || []
                    }
                } else {
                    console.warn('Failed to load FAQs:', response.statusText)
                    this.faqs = []
                }
            } catch (error) {
                console.error('Error loading FAQs:', error)
                this.faqs = []
            } finally {
                this.faqsLoaded = true
                this.faqsLoading = false
            }
        },

        searchFaqs() {
            this.faqsLoaded = false
            this.loadFaqs(true)
        },

        clearFaqSearch() {
            this.faqSearchTerm = ''
            this.faqsLoaded = false
            this.loadAllFaqs()
        },

        onFaqSearchInput() {
            // Clear existing timeout
            if (this.faqSearchTimeout) {
                clearTimeout(this.faqSearchTimeout)
            }

            // Set new timeout for debounced search
            this.faqSearchTimeout = setTimeout(() => {
                this.searchFaqs()
            }, 500) // 500ms delay
        },

        determineDefaultMode() {
            // Priority: AI if enabled, then FAQ list if enabled, then contact
            if (this.aiEnabled) return 'ai'
            if (this.faqListEnabled) return 'faq-list'
            return 'contact'
        },

        handleFaqOpened(faq) {
            this.isViewingFaqDetail = true
            this.$emit('faq-opened', faq)
        },

        handleFaqClosed() {
            this.isViewingFaqDetail = false
            this.$emit('faq-closed')
        },

        handleFaqBackToList() {
            // Call the FaqsList component's back method
            if (this.$refs.faqsList) {
                this.$refs.faqsList.backToList()
            }
        }
    },

    mounted() {
        // If defaultMode is not available due to configuration, determine the best mode
        if ((this.defaultMode === 'ai' && !this.aiEnabled) ||
            (this.defaultMode === 'faq-list' && !this.faqListEnabled)) {
            this.mode = this.determineDefaultMode()
        }

        // Pre-load FAQs if starting in faq-list mode
        if (this.mode === 'faq-list') {
            this.loadAllFaqs()
        }
    },

    beforeDestroy() {
        // Clear any pending search timeout
        if (this.faqSearchTimeout) {
            clearTimeout(this.faqSearchTimeout)
        }
    },

    watch: {
        defaultMode: {
            immediate: true,
            handler(newMode) {
                // Validate the mode is available based on configuration
                if ((newMode === 'ai' && !this.aiEnabled) ||
                    (newMode === 'faq-list' && !this.faqListEnabled)) {
                    this.mode = this.determineDefaultMode()
                } else {
                    this.mode = newMode
                }
            }
        },
        mode: {
            immediate: true,
            handler(newMode) {
                this.$emit('mode-changed', newMode)
            }
        }
    }
}
</script>

<style scoped>
.enquiries-widget {
    position: relative;
}

/* Modal-specific layout */
.enquiries-widget.modal-context {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* Content area - normal behavior by default */
.content-area {
    padding-bottom: 0;
}

/* Modal-specific content area behavior */
.enquiries-widget.modal-context .content-area {
    flex: 1;
    overflow-y: auto;
}

.enquiries-widget.modal-context .content-area.with-toggle {
    padding-bottom: 35px; /* Space for sticky buttons */
}

/* Sticky mode toggle - only sticky in modal context */
.mode-toggle-sticky {
    border-top: 1px solid #e9ecef;
    background: white;
}

/* Modal-specific sticky positioning */
.enquiries-widget.modal-context .mode-toggle-sticky {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    z-index: 5; /* Lower z-index so footer border shows above */
}

/* Mode tabs styling */
.mode-tabs {
    display: flex;
    width: 100%;
}

.mode-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #6c757d;
    background: transparent;
    border: none;
    outline: none;
}

.mode-tab:hover {
    color: #007bff;
    background-color: rgba(0, 123, 255, 0.05);
}

.mode-tab.active {
    color: #007bff;
    background-color: rgba(0, 123, 255, 0.1);
}

.mode-tab:focus {
    outline: 2px solid #007bff;
    outline-offset: -2px;
}

.mode-tab i {
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
    transition: color 0.2s ease;
}

.mode-tab .tab-text {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1;
    transition: color 0.2s ease;
}

.contact-header h5 {
    color: #495057;
}

.contact-header i {
    margin-right: 0.5rem;
}

/* AI Mode needs full height */
.ai-mode {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Remove extra styling from Customer Service Agent when embedded */
.customer-service-clean :deep(.card) {
    border: none;
    box-shadow: none;
}

.customer-service-clean :deep(.card-header) {
    display: none;
}

.customer-service-clean :deep(.card-body) {
    padding: 0;
}

/* Form styling */
.form-group label {
    font-weight: 500;
    color: #495057;
}

/* FAQ Header Area Styling */
.faq-header-area {
    min-height: 20px; /* Consistent height whether showing search or back button */
    margin-bottom: 16px;
}

.faq-back-area {
    display: flex;
    align-items: center;
}

.faq-back-btn {
    color: #6c757d !important;
    font-size: 0.9rem;
    padding: 0.375rem 0;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.faq-back-btn::after {
    display: none !important;
    content: none !important;
}

.faq-back-btn:hover {
    color: #007bff !important;
    text-decoration: none;
}

.faq-back-btn:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.faq-back-btn i {
    font-size: 0.8rem;
}

/* Custom seamless search bar */
.custom-search-bar {
    position: relative;
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1.5px solid #e0e0e0;
    border-radius: 20px;
    padding: 0.4rem 0.75rem;
    transition: all 0.2s ease;
    max-width: 100%;
}

.custom-search-bar:focus-within {
    border-color: #007bff;
    background-color: white;
    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.1);
}

.search-icon {
    color: #999;
    font-size: 0.8rem;
    margin-right: 0.5rem;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.85rem;
}

.search-input::placeholder {
    color: #999;
    font-size: 0.8rem;
}

.clear-btn {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 0.25rem;
    margin-left: 0.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transition: all 0.2s ease;
}

.clear-btn:hover {
    background-color: #007bff;
    color: white;
}

.clear-btn:focus {
    outline: 2px solid #007bff;
    outline-offset: 1px;
}

.clear-btn i {
    font-size: 0.7rem;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .enquiries-widget.modal-context .mode-toggle-sticky {
        margin-left: -1.5rem;
        margin-right: -1.5rem;
        bottom: 0;
    }

    .enquiries-widget.modal-context .content-area.with-toggle {
        padding-bottom: 55px; /* Space for buttons on mobile */
    }

    .d-flex {
        flex-direction: column;
        gap: 0.75rem;
    }

    .d-flex .btn {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Messenger/Messenger.vue